import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import { GenericError } from "@via/components/error.tsx";
import { LogoSpinner } from "@via/components/loading/loading";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { TruncatedText } from "@via/components/truncated-text";
import useUrlParams from "@via/hooks/useURLParams";
import {
  useAppsInstanceList,
  useLaunchApplication,
  useTerminateAppInstance,
} from "@via/pages/apps/apps.api.tsx";
import { getAppInstanceStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  APP_STATUS_TABLE_OPTIONS,
  AppsActions,
  EMPTY_TABLE_MESSAGE,
  ERROR,
  ROUTES,
} from "@via/utils/constants.tsx";
import {
  AppInstanceListResponseBodyItem,
  AppInstanceType,
  AppStatus,
  StartAppType,
} from "common";
import { type JSX, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./apps.css";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const defaultParams = {
  sort: [{ id: "dateCreated", desc: true }],
  page: 0,
  pageSize: 10,
};

const REFETCH_INTERVAL = 10000;

export default function ManageAppStatus(): JSX.Element {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [
    shouldFetchAppsInstanceListInInterval,
    setShouldFetchAppsInstanceListInInterval,
  ] = useState(false);
  const {
    data: appsInstanceList,
    isError: isAppsListInstanceError,
    isLoading: isAppsListInstanceLoading,
  } = useAppsInstanceList(page, pageSize, sort, {
    refetchInterval: shouldFetchAppsInstanceListInInterval
      ? REFETCH_INTERVAL
      : false,
  });
  const {
    mutate: launchAppInstance,
    data: launchAppInstanceData,
    isPending: isLaunchAppInstanceLoading,
    isSuccess: isLaunchAppInstanceSuccess,
  } = useLaunchApplication();
  const { mutate: terminateAppInstance } = useTerminateAppInstance();

  useEffect(() => {
    if (appsInstanceList?.data?.length) {
      const fetchCondition = appsInstanceList.data?.some(
        app => app.status !== AppStatus.Terminated
      );
      setShouldFetchAppsInstanceListInInterval(fetchCondition);
    }
  }, [appsInstanceList]);

  // Detect status transitions
  const previousAppsInstanceListRef = useRef(appsInstanceList);

  useEffect(() => {
    if (!appsInstanceList?.data || !previousAppsInstanceListRef.current?.data) {
      previousAppsInstanceListRef.current = appsInstanceList;
      return;
    }

    const previousApps = previousAppsInstanceListRef.current.data;
    const currentApps = appsInstanceList.data;

    // Detect apps transitioning from "creating" to "running"
    const transitionedApps = currentApps.filter(currentApp => {
      const previousApp = previousApps.find(app => app.id === currentApp.id);
      return (
        previousApp &&
        previousApp.status === AppStatus.Creating &&
        currentApp.status === AppStatus.Running
      );
    });

    if (transitionedApps.length) {
      transitionedApps.forEach(app => {
        app.accessUrl && window.open(app.accessUrl, "_blank", "noreferrer");
      });
    }

    // Update the reference to the current state
    previousAppsInstanceListRef.current = appsInstanceList;
  }, [appsInstanceList]);

  useEffect(() => {
    if (isLaunchAppInstanceSuccess && launchAppInstanceData) {
      const { appStatus, accessUrl } = launchAppInstanceData;
      if (appStatus === AppStatus.Running) {
        window.open(accessUrl, "_blank", "noreferrer");
      }
    }
  }, [isLaunchAppInstanceSuccess, launchAppInstanceData]);

  const getManageAppStatusContext = useCallback(() => {
    const appInstances = appsInstanceList?.data || [];

    return {
      appInstances: appInstances.map(instance => ({
        id: instance.id,
        name: instance.name,
        status: instance.status,
        owner: instance.owner?.username,
        projectPipelineId: instance.projectPipelineId,
        appFile: instance.appFile,
        accessUrl: instance.accessUrl,
        dateCreated: instance.dateCreated,
      })),
      total: appsInstanceList?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [appsInstanceList?.data, appsInstanceList?.total, page, pageSize, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.APP_MANAGE_STATUS,
    contextName: "Manage App Status",
    getContext: getManageAppStatusContext,
    systemPrompt:
      "You are analyzing app status list page. Help answer questions about the app status list page.",
  });

  const handleContextMenuAction = (
    name: string,
    row: AppInstanceListResponseBodyItem
  ): void => {
    switch (name) {
      case AppsActions.Launch: {
        const { type, appId, appFile, appDir, projectPipelineId, runUUID } =
          row;
        if (type === AppInstanceType.report) {
          launchAppInstance({
            id: appId,
            payload: {
              dir: appDir,
              runUUID,
              filename: appFile,
              projectPipelineId,
              type: StartAppType.Report,
            },
          });
        } else {
          launchAppInstance({
            id: row.appId,
            payload: { type: StartAppType.Standalone },
          });
        }
        break;
      }
      case AppsActions.Open:
        window.open(row.accessUrl, "_blank", "noreferrer");
        break;
      case AppsActions.Terminate:
        terminateAppInstance(row.id);
        break;
    }
  };

  const columnHelper = createColumnHelper<AppInstanceListResponseBodyItem>();
  const columns = [
    columnHelper.accessor("name", {
      size: 300,
      cell: ({ row }) => {
        return (
          <div className="truncate">
            <TruncatedText
              text={row.original.name}
              maxLength={60}
              id={`app-name-${row.original.id}-${row.original.name}`}
            />
          </div>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("projectPipelineId", {
      cell: info => (
        <Link
          to={`/runs/${info.row.original?.projectPipelineId}`}
          className="via-link truncate">
          {info?.getValue()}
        </Link>
      ),
      header: () => <span>Run Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("appFile", {
      cell: info => (
        <TruncatedText
          text={info.getValue() || ""}
          maxLength={20}
          id={`tooltip-${info.column.id}-${info.row.original.id}`}
        />
      ),
      header: () => <span>App File</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("status", {
      cell: ({ row }) => {
        return <>{getAppInstanceStatusLabel(row.original.status)}</>;
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor("owner", {
      cell: ({ row }) => {
        return (
          <div className="truncate">
            <TruncatedText
              text={row.original.owner?.username}
              maxLength={30}
              id={`app-owner-${row.original.id}-${row.original.owner}`}
            />
          </div>
        );
      },
      header: () => "Owner",
      enableSorting: false,
      size: 200,
    }),
    columnHelper.accessor("id", {
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.status === AppStatus.Running) {
          return (
            <TableContextMenu
              options={[
                APP_STATUS_TABLE_OPTIONS.TERMINATE,
                APP_STATUS_TABLE_OPTIONS.OPEN,
              ]}
              onSelect={option => {
                handleContextMenuAction(option.name, row.original);
              }}
            />
          );
        } else if (row.original.status === AppStatus.Creating) {
          return (
            <ActionButton
              button={APP_STATUS_TABLE_OPTIONS.TERMINATE.name}
              primary={true}
              showIcon={false}
              onClick={() => {
                handleContextMenuAction(
                  APP_STATUS_TABLE_OPTIONS.TERMINATE.name,
                  row.original
                );
              }}
            />
          );
        } else {
          return (
            <ActionButton
              button={APP_STATUS_TABLE_OPTIONS.LAUNCH.name}
              primary={true}
              showIcon={false}
              onClick={() => {
                handleContextMenuAction(
                  APP_STATUS_TABLE_OPTIONS.LAUNCH.name,
                  row.original
                );
              }}
            />
          );
        }
      },
      header: () => "Options",
    }),
  ] as Array<ColumnDef<AppInstanceListResponseBodyItem>>;

  useEffect(() => {
    setUrlParams({ sort, page, pageSize });
  }, [sort, page, pageSize]);

  return (
    <main className="py-4">
      {isLaunchAppInstanceLoading ? (
        <div className="via-loading-spinner">
          <LogoSpinner />
        </div>
      ) : isAppsListInstanceError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isAppsListInstanceLoading}
          data={appsInstanceList?.data}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          applyColumnSize
          total={appsInstanceList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.APP_HISTORY}
          size={TableSize.Big}
        />
      )}
    </main>
  );
}
